import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import '../Chat.css';
import Box from '../StyledHtmlElements/Box';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { RefreshCw } from 'lucide-react';

export type Message = {
  id: string;
  content: string;
  role: 'user' | 'bot';
  references?: { text: string; url: string }[];
  renderMessage?: (content: string, references?: { text: string; url: string }[]) => JSX.Element;
  error?: string;
};

export type OnReferenceClick = (citation: { text: string; url: string }) => void;

type Props = {
  messages: Message[];
  messageLoading: boolean;
  className?: string;
  onReferenceClick?: OnReferenceClick;
  onSendMessage: (message: string, isRetrying: boolean) => void;
};

const MessageBox = ({ messages, onReferenceClick, messageLoading, onSendMessage, className = '' }: Props) => {
  const chatBoxRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState<boolean>(false);

  const messageRenderer = (content: string) => {
    if (!content) return null;

    const isHtml = /^<[^>]+>/.test(content.trim());

    if (isHtml) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    return (
      <div className="flex flex-col justify-start items-start">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    );
  };

  const handleUserScroll = () => {
    if (chatBoxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // Add a small threshold
      if (!isAtBottom) {
        setIsUserScrolling(true); // User is actively scrolling
      } else {
        setIsUserScrolling(false); // Auto-scroll can proceed
      }
    }
  };

  const handleUserRetry = () => {
    const lastUserMessage =
      messages
        .slice()
        .reverse()
        .find((message) => message.role === 'user')?.content || '';

    if (!lastUserMessage) return;

    onSendMessage(lastUserMessage, true);
  };

  useEffect(() => {
    if (!isUserScrolling) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isUserScrolling]);

  return (
    <Box ref={chatBoxRef} onScroll={handleUserScroll} className={cn('grow-[5] px-[15%] overflow-y-auto ', className)}>
      <div className="flex flex-col gap-8 ">
        {messages.map((message, index) => {
          if (!message.content && !message.references) return null;
          if (message.content && message.error && messageLoading) return null;
          return (
            <div
              key={message.id + index}
              className={cn(
                'rounded-lg p-2 my-1 bg-secondary/30',
                message.role === 'user'
                  ? 'self-end  text-neutralDark ml-12'
                  : 'self-start bg-secondary/70 dark:bg-secondary/10 text-neutralDark mr-12',
                message.error && !messageLoading ? '!bg-red-800/70' : ''
              )}>
              <span className="flex items-center">
                {message.role === 'user' ? (
                  <>
                    <span className="px-2 break-all text-m text-neutralDark-900">
                      {message.renderMessage
                        ? message.renderMessage(message.content)
                        : messageRenderer(message.content)}
                    </span>
                  </>
                ) : (
                  <div className="px-4 text-m text-neutralDark-900 rounded-lg items-center flex gap-4 ">
                    {!message.error
                      ? message.renderMessage
                        ? message.renderMessage(message.content)
                        : messageRenderer(message.content)
                      : index == messages.length - 1 &&
                        !messageLoading && (
                          <>
                            {message.renderMessage
                              ? message.renderMessage(message.content)
                              : messageRenderer(message.content)}
                            <span
                              className="bg-background border border-border px-4 py-0.5 rounded-lg flex items-center gap-2 cursor-pointer"
                              onClick={handleUserRetry}>
                              Erneut versuchen <RefreshCw size={14} />
                            </span>
                          </>
                        )}
                  </div>
                )}
              </span>

              {message.references && message.references.length ? (
                <div className="flex flex-col mt-4">
                  <p>Referenzen: </p>

                  <ul className="flex gap-1">
                    {message.references.map((references, i) => (
                      <li
                        key={i}
                        onClick={() => onReferenceClick && onReferenceClick(references)}
                        className="cursor-pointer text-primary text-base"
                        data-index={i}>
                        <a href={references.url} target="_blank" rel="noopener noreferrer">
                          {references?.url?.split('/').pop() || i + 1}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}

        {(messages.length && messageLoading && messages[messages.length - 1].role !== 'bot') ||
        (messages.length &&
          messages[messages.length - 1].role === 'bot' &&
          (messages[messages.length - 1].content === '' || (messageLoading && messages[messages.length - 1].error))) ? (
          <div className="self-start text-neutralDark flex rounded-lg p-2 my-1 bg-secondary/70 dark:bg-secondary/10  mr-12">
            <div className="robot-icon-container m-auto">
              <div className="loader"></div>
              <FontAwesomeIcon icon={faRobot} className="robot-icon" />
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>
    </Box>
  );
};

export default MessageBox;
